import React, { useMemo, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Divider,
    Grid,
    Typography,
    alpha,
    Autocomplete,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { useClients } from '../../../hooks/comptable/useClients';

const IDENTIFIER_OPTIONS = [
    { value: 1, label: 'Matricule fiscale' },
    { value: 2, label: 'CIN' },
    { value: 3, label: 'Passport' },
    { value: 4, label: 'Carte de sejour' },
    { value: 5, label: 'Identifiant etranger' },
];

const CertificationPreviewSection = ({
    verificationData,
    setVerificationData,
    beneficiary,
    setBeneficiary,
    onContinue,
    isVisible,
    palette,
    fixedClientId,
}) => {
    const { loading: authLoading, isAuthenticated } = useAuth();
    const { clients, loading: clientsLoading } = useClients();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const selectedClient = useMemo(() => {
        if (!verificationData.selectedClientId) return null;
        return clients.find(c => c._id === verificationData.selectedClientId) || null;
    }, [verificationData.selectedClientId, clients]);

    const selectedLabel = useMemo(() => {
        return (
            IDENTIFIER_OPTIONS.find(
                (item) => item.value === verificationData.identifierType
            )?.label || 'Identifiant'
        );
    }, [verificationData.identifierType]);

    const handleFieldChange = (field, value) => {
        setVerificationData((prev) => ({ ...prev, [field]: value }));
    };

    // Masque matricule fiscale : 0000000/X (7 chiffres + / + 1 lettre)
    const handleMatriculeChange = (e) => {
        const raw = e.target.value.replace(/[^0-9a-zA-Z]/g, '');
        const digits = raw.slice(0, 7).replace(/[^0-9]/g, '');
        const letter = raw.slice(7, 8).replace(/[^a-zA-Z]/g, '').toUpperCase();
        let formatted = digits;
        if (digits.length === 7 && letter) {
            formatted = `${digits}/${letter}`;
        } else if (digits.length === 7) {
            formatted = `${digits}/`;
        }
        handleFieldChange('identifier', formatted);
    };

    const handleVerify = async () => {
        setError('');
        setBeneficiary(null);

        const trimmedIdentifier = verificationData.identifier.trim();

        if (!isAuthenticated) {
            setError('Utilisateur non authentifie.');
            return;
        }
        if (!verificationData.identifierType) {
            setError("Veuillez selectionner un type d'identifiant.");
            return;
        }
        if (!verificationData.selectedClientId) {
            setError('Veuillez selectionner un client.');
            return;
        }
        if (!trimmedIdentifier) {
            setError('Veuillez saisir une valeur.');
            return;
        }

        try {
            setLoading(true);
            const response = await axios.post(
                `/api/certif/beneficiary/verify/${verificationData.selectedClientId}`,
                {
                    identifierType: verificationData.identifierType,
                    identifier: trimmedIdentifier,
                }
            );
            setBeneficiary(response.data?.data || null);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                'Erreur lors de la verification du beneficiaire.'
            );
        } finally {
            setLoading(false);
        }
    };

    if (!isVisible) return null;

    if (authLoading) {
        return (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    const readOnlyFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: alpha(palette.navy, 0.04),
        },
    };

    return (
        <Stack spacing={2.5}>
            {!fixedClientId && <Autocomplete
                fullWidth
                options={clients}
                value={selectedClient}
                onChange={(_, newValue) => handleFieldChange('selectedClientId', newValue?._id || '')}
                getOptionLabel={(option) => {
                    const name = option.raisonSociale || `${option.prenom || ''} ${option.nom || ''}`.trim();
                    const code = option.codeClient || '';
                    return code ? `${name} (${code})` : name;
                }}
                filterOptions={(options, { inputValue }) => {
                    const search = inputValue.toLowerCase().trim();
                    if (!search) return options;
                    return options.filter((c) => {
                        const name = (c.raisonSociale || `${c.prenom || ''} ${c.nom || ''}`).toLowerCase();
                        const code = (c.codeClient || '').toLowerCase();
                        return name.includes(search) || code.includes(search);
                    });
                }}
                loading={clientsLoading}
                noOptionsText="Aucun client trouve"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Client"
                        placeholder="Rechercher par nom ou code client..."
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {clientsLoading ? <CircularProgress size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
            />}

            <FormControl fullWidth>
                <InputLabel id="identifier-type-label">Type d'identifiant</InputLabel>
                <Select
                    labelId="identifier-type-label"
                    value={verificationData.identifierType}
                    label="Type d'identifiant"
                    onChange={(e) => handleFieldChange('identifierType', Number(e.target.value))}
                >
                    {IDENTIFIER_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                fullWidth
                label={selectedLabel}
                value={verificationData.identifier}
                onChange={verificationData.identifierType === 1 ? handleMatriculeChange : (e) => handleFieldChange('identifier', e.target.value)}
                placeholder={verificationData.identifierType === 1 ? '0000000/X' : `Entrer ${selectedLabel.toLowerCase()}`}
                inputProps={verificationData.identifierType === 1 ? { maxLength: 9 } : {}}
                helperText={verificationData.identifierType === 1 ? 'Format : 7 chiffres / 1 lettre' : ''}
            />

            <Box>
                <Button
                    variant="contained"
                    onClick={handleVerify}
                    disabled={loading}
                    sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: 2,
                        fontWeight: 700,
                        textTransform: 'none',
                        background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)`,
                        boxShadow: '0 4px 20px rgba(24, 182, 176, 0.3)',
                        '&:hover': {
                            background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)`,
                        },
                    }}
                >
                    {loading ? <CircularProgress size={22} color="inherit" /> : 'Verifier'}
                </Button>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {beneficiary && (
                <>
                    <Divider />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: palette.textPrimary, mb: 3 }}>
                            Informations du beneficiaire
                        </Typography>

                        <Grid container spacing={2.5}>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth label="Matricule fiscal" value={beneficiary.identifier || ''} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth label="Nom et prenom/Raison sociale" value={beneficiary.nometprenonOuRaisonsociale || ''} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth label="Activite" value={beneficiary.activity || ''} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth label="Adresse" value={beneficiary.address || ''} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth label="Email" value={beneficiary.email || ''} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField fullWidth label="Numero de telephone" value={beneficiary.phoneNumber || ''} InputProps={{ readOnly: true }} sx={readOnlyFieldSx} />
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                onClick={onContinue}
                                sx={{
                                    px: 3,
                                    py: 1.2,
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)`,
                                    boxShadow: '0 4px 20px rgba(24, 182, 176, 0.3)',
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)`,
                                    },
                                }}
                            >
                                Continuer
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </Stack>
    );
};

export default CertificationPreviewSection;
